import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Message } from 'semantic-ui-react'
import MilkMoneyAccountingService from '~/api/services/milkMoneyAccounting'
import Dialog from '~/components/Dialog'
import { FormFields } from '~/components/FormFields'
import notify from '~/components/Notify'
import TableComponent from '~/components/TableComponent'
import i18n from '~/components/i18n'

interface UpdatePricesProps {
  isDialogOpen: boolean
  handleClose: () => void
  handleSubmit: () => void
  control: any
  article: any
  isUploading: boolean
  oldValue: any
}

const getTableColumnHeaders = () => [
  {
    name: i18n.t('Booking date'),
    type: 'date',
    format: 'DD.MM.YYYY HH:mm',
    key: 'bookingDate'
  },
  {
    name: i18n.t('Supplier No.'),
    key: 'supplierNo'
  },
  {
    name: i18n.t('Company name'),
    key: 'companyName'
  },
  {
    name: i18n.t('Amount'),
    key: 'amount',
    type: 'no-unit-number',
    decimalScale: 0
  },
  {
    name: i18n.t('Unit'),
    key: 'unitLabel'
  }
]

const UpdatePrices: React.FC<UpdatePricesProps> = (props) => {
  const { isDialogOpen, handleSubmit, handleClose, control, article, oldValue } = props
  const { id } = useParams()
  const { t } = useTranslation()
  const watchUpdatePriceOnBookings = useWatch({ control, name: 'updatePriceOnBookings' })

  const [articleBookings, setArticleBookings] = useState([])
  const [loadingBookings, setLoadingBookings] = useState(false)
  const [date, setDate] = useState('')

  useEffect(() => {
    if (watchUpdatePriceOnBookings) {
      fetchArticles()
    }
  }, [watchUpdatePriceOnBookings])

  async function fetchArticles() {
    setLoadingBookings(true)
    try {
      const response = await MilkMoneyAccountingService.getAriclesBookings(id)
      setArticleBookings(response.data?.articleBookings)
      setDate(dayjs(response.data.date).format('DD.MM.YYYY'))
    } catch (error) {
      if (error.response.data) {
        notify(error.response.data.message, 'error')
      } else {
        notify(t('General error'), 'error')
      }
    }
    setLoadingBookings(false)
  }

  function renderTable() {
    return <TableComponent
      total={articleBookings.length}
      rows={articleBookings}
      removePagination
      tableColumnHeaders={getTableColumnHeaders()}
    />
  }

  return <Dialog
    isDialogOpen={isDialogOpen}
    handleClose={() => handleClose()}
    isLoading={loadingBookings || props.isUploading}
    handleSubmit={() => handleSubmit()}
    typeButton={'submit'}
    textContent={<>
      <FormFields.Checkbox
        name={'updatePriceOnBookings'}
        control={control}
        defaultValue={0}
        checkboxValue={1}
        label={t('Do you want to change the price for already booked items?')}
      />
      {Boolean(watchUpdatePriceOnBookings) && renderTable()}
      {Boolean(watchUpdatePriceOnBookings) && <Message info>
        <p>{t('You have changed the net price (in €) for the article {{article}} from {{oldPrice}} to {{newPrice}}. \n\
        The following existing bookings will be based on the new net price (in €) {{price}} from {{date}}', {
          article: `${article?.name} (${article?.uniqueId})`,
          oldPrice: oldValue,
          newPrice: article?.netPrice,
          price: article?.netPrice,
          date: date
        }
        )
        }</p>
      </Message>}
    </>}
    textClose={t('Cancel')}
    textSubmit={t('Update')}
    textHeader={t('Update existing bookings')}
    type={watchUpdatePriceOnBookings ? 'dialog' : 'abort'}
  />
}
export default UpdatePrices